import {
  useGetAccountCorrespondenceLetterQuery,
  useGetWelcomeKitQuery,
} from "@apis/accounts/useAccounts"

type TCorrespondenceResolverParams = {
  account_id: string
  account_correspondence_id: number
  isWelcome: boolean
}

type TCorrespondenceResolverReturn = {
  // For SendMail/SendEmail: resolves the correct correspondence_id
  // - isWelcome=true  → fetches welcomeKit → returns data.account_correspondence_id
  // - isWelcome=false → returns account_correspondence_id prop directly (no fetch)
  getCorrespondenceId: () => Promise<number | null>

  // For ViewLetter: resolves the correct letter URL
  // - isWelcome=true  → fetches welcomeKit           → returns data.url
  // - isWelcome=false → fetches correspondenceLetter → returns data.url
  getLetterUrl: () => Promise<string | null>

  // true when any of the queries is actively fetching
  isCorrespondenceResolverFetching: boolean
}

export const useCorrespondenceResolver = ({
  account_id,
  account_correspondence_id,
  isWelcome,
}: TCorrespondenceResolverParams): TCorrespondenceResolverReturn => {
  const getWelcomeKitQuery = useGetWelcomeKitQuery(account_id)
  const getAccountCorrespondenceLetterQuery =
    useGetAccountCorrespondenceLetterQuery({
      account_id,
      account_correspondence_id,
    })

  const getCorrespondenceId = async (): Promise<number | null> => {
    if (!isWelcome) return account_correspondence_id

    const res = await getWelcomeKitQuery.refetch()
    if (!res.isSuccess) return null

    return res.data.account_correspondence_id
  }

  const getLetterUrl = async (): Promise<string | null> => {
    const res = isWelcome
      ? await getWelcomeKitQuery.refetch()
      : await getAccountCorrespondenceLetterQuery.refetch()

    if (!res.isSuccess) return null

    return res.data.url
  }

  return {
    getCorrespondenceId,
    getLetterUrl,
    isCorrespondenceResolverFetching:
      getWelcomeKitQuery.isFetching ||
      getAccountCorrespondenceLetterQuery.isFetching,
  }
}
